import { createContext, useContext, useEffect, useState } from "react";
import { useApiClient } from "../../providers/AuthProvider";

export type ClientSettings = {
  enabled: boolean;
  requireTwoFactor: boolean;
  userLimit: number;
  deviceLimit: number;
  allowCrossTenantSchedules: boolean;
};

export type Client = {
  id: string;
  name: string;
  settings: ClientSettings;
  userCount: number;
  deviceCount: number;
  siteCount: number;
  createdAt: string;
};

type ClientsContextType = {
  clients: Client[];
  loading: boolean;
  error: string | null;
  fetchClients: () => Promise<void>;
  updateClient: (id: string, data: { name?: string; settings?: Partial<ClientSettings> }) => Promise<Client>;
};

const ClientsContext = createContext<ClientsContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

export const ClientsProvider = ({ children }: Props) => {
  const apiClient = useApiClient();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<{ clients: Client[] }>("/api/clients");
      setClients(data.clients);
    } catch (err: any) {
      setError(err.message ?? "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const updateClient = async (
    id: string,
    data: { name?: string; settings?: Partial<ClientSettings> },
  ): Promise<Client> => {
    const result = await apiClient.put<{ client: Client }>(`/api/clients/${id}`, data);
    setClients((current) => current.map((client) => (client.id === id ? result.client : client)));
    return result.client;
  };

  useEffect(() => {
    void fetchClients();
  }, [apiClient]);

  const value: ClientsContextType = {
    clients,
    loading,
    error,
    fetchClients,
    updateClient,
  };

  return <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>;
};

export const useClients = (): ClientsContextType => {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error("useClients must be used within a ClientsProvider");
  }
  return context;
};
